import { TableChangeSource } from '../../../../../../common/table/TableTypes';
import { applyManualColumnWidthsWithHotHooks } from '../columnSize/hotTableManualColumnResizeApplyUtils';
import { getHotTableColumnWidthsPx } from '../columnSize/hotTableManualColumnResizeUtils';
import { HotTableUndoRedoActionType } from './hotTableUndoRedoRichCellDataUtils';
import { MNHotInstance } from '../../../tableHotInstanceTypes';

type UndoRedoCallback = () => void;

type ColumnWidthChange = {
    column: number;
    widthPx: number;
};

export type HotTableColumnResizeUndoRedoAction = {
    actionType: typeof HotTableUndoRedoActionType.MILANOTE_COLUMN_RESIZE;
    undo: (instance: MNHotInstance, callback: UndoRedoCallback) => void;
    redo: (instance: MNHotInstance, callback: UndoRedoCallback) => void;
};

type HotUndoRedoPluginWithDone = {
    done?: (wrappedAction: () => HotTableColumnResizeUndoRedoAction, source?: string) => void;
};

/**
 * Captures the widths for specific visual columns from a full HOT width snapshot.
 */
export const createColumnWidthChangesFromSnapshot = (
    columnWidthsPx: number[],
    columns: number[],
): ColumnWidthChange[] =>
    columns.map((column) => ({
        column,
        widthPx: columnWidthsPx[column],
    }));

/**
 * Applies column widths through HOT's resize hooks so Milanote persistence and editor layout stay in sync.
 */
const applyColumnWidthSnapshot = (
    hotInstance: MNHotInstance,
    columnWidthChanges: ColumnWidthChange[],
    callback: UndoRedoCallback,
): void => {
    applyManualColumnWidthsWithHotHooks(hotInstance, columnWidthChanges);

    requestAnimationFrame(() => {
        callback();
    });
};

/**
 * Pushes a Milanote-owned column resize action onto Handsontable's undo stack.
 */
export const addHotTableColumnResizeUndoAction = ({
    hotInstance,
    beforeColumnWidthsPx,
    columns,
}: {
    hotInstance: MNHotInstance;
    beforeColumnWidthsPx: number[];
    columns: number[];
}): void => {
    if (!columns.length) return;

    const undoRedoPlugin = hotInstance.getPlugin('undoRedo') as HotUndoRedoPluginWithDone | undefined;
    if (!undoRedoPlugin?.done) return;

    const afterColumnWidthsPx = getHotTableColumnWidthsPx(hotInstance);
    const resizedColumns = columns.filter((column) => beforeColumnWidthsPx[column] !== afterColumnWidthsPx[column]);

    if (!resizedColumns.length) return;

    const beforeColumnWidthChanges = createColumnWidthChangesFromSnapshot(beforeColumnWidthsPx, resizedColumns);
    const afterColumnWidthChanges = createColumnWidthChangesFromSnapshot(afterColumnWidthsPx, resizedColumns);

    undoRedoPlugin.done(
        (): HotTableColumnResizeUndoRedoAction => ({
            actionType: HotTableUndoRedoActionType.MILANOTE_COLUMN_RESIZE,
            undo: (instance, undoneCallback) => {
                applyColumnWidthSnapshot(instance, beforeColumnWidthChanges, undoneCallback);
            },
            redo: (instance, redoneCallback) => {
                applyColumnWidthSnapshot(instance, afterColumnWidthChanges, redoneCallback);
            },
        }),
        TableChangeSource.COLUMN_RESIZE_REPLAY,
    );
};
